import { getPatronByCisId, getPatronByPatronId } from '../../../services/artifactService'
import {
  getCisIdAndContributorIdPair,
  getCisIdFromUsername,
  getContactNameSearchResult,
} from '../../../services/userService'

/**
 * The Memories backend works with Memories Patron Uploader IDs.
 * These functions translate other user ID types into Memories Patron IDs.
 */

/**
 * Translates a user query (a string with a prefix and identifier, such as “mp.19” or “cis.user.MMMM-MMMM”)
 * into a Memories Patron Uploader ID.
 *
 * @export
 * @param {string} userQuery containing prefix of cis.user., cis.wkca., un., mp., u., or cn. and ID
 * @returns {?number} patronId (or null if userQuery was invalid)
 */
export async function getPatronByUserQuery(userQuery, userQueryIsPatronId) {
  if (userQueryIsPatronId) {
    return getPatronByPatronId(userQuery)
  }

  // Ignore case, spaces, and commas when entering an ID
  // Ignore spaces and commas when entering an ID
  userQuery = userQuery.replace(/[ ,]/g, '')

  // Check for search prefix. Match returns [entire, prefix, id]
  const prefixMatch = userQuery.match(/(.+)\.(.+)/)
  if (prefixMatch !== null && prefixMatch.length >= 3) {
    const prefix = prefixMatch[1].toLowerCase()
    const id = prefixMatch[2]

    try {
      switch (prefix) {
        case 'cis.user': // CIS User ID: “cis.user.MMMM-MMMM”
        case 'cis.wkca': // CIS WKCA ID: “cis.wkca.MMMM-MMMM”
          return getMemoriesPatronIdFromCisId(`${prefix}.${id}`)
        case 'un': // User Name: “un.someusername”
          return getMemoriesPatronIdFromUserName(id)
        case 'mp': // Memories Patron ID: “mp.123456”
          return getPatronByPatronId(id)
        case 'u': // Tree Contributor ID: go to patron artifacts page for “u.MMDZ-11L” or “u.10000123”
          return getMemoriesPatronIdFromContributorId(id)
        case 'cn': // Contact Name: go to patron artifacts page for “cn.mycontactname”
          return getMemoriesPatronIdFromContactName(id)
        default:
          // Unsupported search prefix
          return null
      }
    } catch (error) {
      console.error(`Error getting memories patron ID from userQuery ${userQuery}`, error)
      return null
    }
  }
  return null
}

export function getMemoriesPatronIdFromCisId(cisId) {
  // If no ‘cis.’ prefix, assume it is a user
  if (!cisId.includes('cis.')) {
    cisId = `cis.user.${cisId}`
  }
  return getPatronByCisId(cisId)
}

export async function getMemoriesPatronIdFromUserName(userName) {
  return getCisIdFromUsername(userName)
    .then((response) => {
      return getMemoriesPatronIdFromCisId(response.data)
    })
    .catch((err) => {
      throw err
    })
}

export async function getMemoriesPatronIdFromContributorId(contributorId) {
  return getCisIdAndContributorIdPair(contributorId, 'contributorId')
    .then((response) => {
      return getMemoriesPatronIdFromCisId(response.data[0].id)
    })
    .catch((err) => {
      throw err
    })
}

export async function getMemoriesPatronIdFromContactName(contactName) {
  return getContactNameSearchResult(contactName)
    .then((response) => {
      return getMemoriesPatronIdFromCisId(response.data.users[0].id)
    })
    .catch((err) => {
      throw err
    })
}
